import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  ERLEDIGUNGSNAME,
  liste as ladeListe,
  verwerfen,
  zuordnen,
  zuruecknehmen,
  type Kontozuordnung,
  type Zuordnungskandidat,
} from '../api/kontozuordnungen';
import { ApiFehler } from '../api/fehler';
import { alsZeitpunkt } from '../datum';
import { markenklasse, type Markenart } from '../marken';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/**
 * Die Zuordnung neuer Online-Konten (WEB-10 i.d.F. AeA-37).
 *
 * Mit der Bestätigung der Adresse ordnet das System ein Konto dem Kunden zu,
 * der diese Adresse führt — wenn es genau einer ist. Sind es mehrere, landet
 * der Fall hier, und der Innendienst wählt einen Treffer oder verwirft. Jede
 * Zuordnung steht im Protokoll und lässt sich mit Grund zurücknehmen.
 *
 * SEITENTYP LISTE (ÄA-41): je Fall eine Zeile, der gewählte Fall mit seinen
 * Treffern und Handlungen in der Vorschau rechts. Gewählt ist anfangs der
 * erste — der älteste offene Fall ist der nächste, der zu tun ist.
 *
 * ZUORDNEN, VERWERFEN UND ZURÜCKNEHMEN FRAGEN IM POP-UP NACH (CRM-104): Die
 * Zuordnung öffnet einem Online-Konto den Kunden, das Verwerfen schließt den
 * Fall, und nach der Rücknahme verbindet nichts die beiden wieder. Der Grund
 * bleibt im Feld der Vorschau; das Pop-up zeigt ihn an.
 */

type Nachfrage =
  | { readonly art: 'zuordnen'; readonly fall: Kontozuordnung; readonly kandidat: Zuordnungskandidat }
  | { readonly art: 'verwerfen' | 'zuruecknehmen'; readonly fall: Kontozuordnung; readonly grund: string };

/** Der Stand eines Falls als Wort — dieselben Wörter wie im Protokoll. */
function stand(z: Kontozuordnung): string {
  if (z.offen) return 'offen';
  if (z.ergebnis === 'automatisch' && z.erledigung === null) return 'automatisch zugeordnet';
  if (z.erledigung === null) return 'mehrdeutig';
  return ERLEDIGUNGSNAME[z.erledigung];
}

/** Offen ist zu tun (WEB-10); zugeordnet ist am Ziel; verworfen und zurückgenommen sind still. */
function standart(z: Kontozuordnung): Markenart {
  if (z.offen) return 'aufmerksamkeit';
  if (z.erledigung === 'verworfen' || z.erledigung === 'zurueckgenommen') return 'stillgelegt';
  return 'abschluss';
}
export function Kontozuordnungen({
  darfBearbeiten,
  beiKunde,
}: {
  readonly darfBearbeiten: boolean;
  readonly beiKunde: (id: string) => void;
}): JSX.Element {
  const [alle, setzeAlle] = useState(false);
  const [liste, setzeListe] = useState<readonly Kontozuordnung[]>([]);
  const [geladen, setzeGeladen] = useState(false);
  const [gewaehlt, setzeGewaehlt] = useState<string | null>(null);
  const [grund, setzeGrund] = useState<Readonly<Record<string, string>>>({});
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [nachfrage, setzeNachfrage] = useState<Nachfrage | null>(null);

  const melde = useCallback((e: unknown) => {
    if (e instanceof ApiFehler) setzeFehler(e);
    else throw e;
  }, []);

  const holen = useCallback(async () => {
    try {
      setzeListe(await ladeListe(alle));
      setzeFehler(null);
    } catch (e) {
      melde(e);
    } finally {
      setzeGeladen(true);
    }
  }, [alle, melde]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /*
   * Wirft (CRM-104): Aus der Rückfrage heraus steht ein Fehler im Pop-up, und
   * es bleibt offen. Scheitert erst das Nachladen, meldet `holen` es auf der
   * Seite — die Entscheidung ist dann schon geschehen.
   */
  const entscheiden = async (vorgang: () => Promise<Kontozuordnung>): Promise<void> => {
    await vorgang();
    setzeNachfrage(null);
    setzeGrund({});
    await holen();
  };

  const grundZu = (id: string): string => grund[id] ?? '';

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(
    !geladen
      ? undefined
      : alle
        ? zaehlwort(liste.length, 'Zuordnung', 'Zuordnungen')
        : zaehlwort(liste.length, 'offener Fall', 'offene Fälle'),
  );

  /* Verschwindet der gewählte Fall nach einer Entscheidung, rückt der erste nach. */
  const fall = liste.find((z) => z.id === gewaehlt) ?? liste[0];

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Liste
        vorschauBeschriftung="Gewählter Fall"
        filter={
          <label className="liste__schalter">
            <input
              type="checkbox"
              checked={alle}
              onChange={(e) => {
                setzeAlle(e.target.checked);
              }}
            />
            <span>Alle Zuordnungen zeigen (Protokoll)</span>
          </label>
        }
        hinweise={
          <p className="hilfe">
            Neue Online-Konten, deren Adresse mehr als ein Kunde führt. Wählen Sie den richtigen
            Treffer oder verwerfen Sie den Fall; dann entsteht der Kunde mit der ersten Buchung.
          </p>
        }
        vorschau={
          fall === undefined ? undefined : (
            <section aria-label={`Kontozuordnung ${fall.email}`}>
              <div className="karte__titel">
                <h2>{fall.email}</h2>
                <small>{alsZeitpunkt(fall.angelegtAm)}</small>
              </div>
              {fall.offen ? (
                <>
                  <p>{String(fall.kandidaten.length)} Kunden führen diese Adresse:</p>
                  <ul className="kanalliste" aria-label="Treffer">
                    {fall.kandidaten.map((k) => (
                      <li key={k.kunde}>
                        <button
                          type="button"
                          className="liste__oeffnen"
                          onClick={() => {
                            beiKunde(k.kunde);
                          }}
                        >
                          {k.kundennummer} {k.name}
                        </button>{' '}
                        <span className={markenklasse('laufend')}>
                          {k.ueber
                            .map((u) => (u === 'kunde' ? 'am Kunden' : 'am Ansprechpartner'))
                            .join(', ')}
                        </span>
                        {/* WEB-10: ein Kunde trägt höchstens ein Online-Konto. */}
                        {k.andersVerbunden && (
                          <span className={markenklasse('aufmerksamkeit')}>hat schon ein Konto</span>
                        )}
                        {darfBearbeiten && !k.andersVerbunden && (
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            onClick={() => {
                              setzeNachfrage({ art: 'zuordnen', fall, kandidat: k });
                            }}
                          >
                            Diesem Kunden zuordnen …
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p role="status">
                  {stand(fall)}
                  {fall.kunde !== null && (
                    <>
                      {' '}
                      an{' '}
                      <button
                        type="button"
                        className="liste__oeffnen"
                        onClick={() => {
                          beiKunde(fall.kunde!.id);
                        }}
                      >
                        {fall.kunde.kundennummer} {fall.kunde.name}
                      </button>
                    </>
                  )}
                  {fall.erledigtAm !== null && <> am {alsZeitpunkt(fall.erledigtAm)}</>}
                  {fall.grund !== null && <> — {fall.grund}</>}
                </p>
              )}
              {/* AeA-37: Verwerfen und Zurücknehmen nur mit Grund. */}
              {darfBearbeiten && (fall.offen || fall.ruecknehmbar) && (
                <>
                  <label className="feld">
                    <span className="feld__beschriftung">Grund</span>
                    <input
                      value={grundZu(fall.id)}
                      maxLength={500}
                      onChange={(e) => {
                        setzeGrund({ ...grund, [fall.id]: e.target.value });
                      }}
                    />
                  </label>
                  <div className="knopfreihe">
                    <button
                      type="button"
                      className="knopf knopf--eingriff"
                      disabled={grundZu(fall.id).trim() === ''}
                      onClick={() => {
                        setzeNachfrage({
                          art: fall.offen ? 'verwerfen' : 'zuruecknehmen',
                          fall,
                          grund: grundZu(fall.id).trim(),
                        });
                      }}
                    >
                      {fall.offen ? 'Verwerfen …' : 'Zuordnung zurücknehmen …'}
                    </button>
                  </div>
                </>
              )}
            </section>
          )
        }
      >
        {liste.length === 0 ? (
          geladen && (
            <div className="liste__leer" role="status">
              <p>{alle ? 'Noch keine Zuordnungen.' : 'Keine offenen Fälle.'}</p>
            </div>
          )
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Kontozuordnungen">
              <thead>
                <tr>
                  <th scope="col">Adresse</th>
                  <th scope="col">Eingang</th>
                  <th scope="col">Kunde</th>
                  <th scope="col">Stand</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((z) => (
                  <tr
                    key={z.id}
                    className={fall?.id === z.id ? 'liste__zeile--gewaehlt' : undefined}
                  >
                    <th scope="row">
                      <button
                        type="button"
                        className="liste__oeffnen"
                        aria-pressed={fall?.id === z.id}
                        onClick={() => {
                          setzeGewaehlt(z.id);
                        }}
                      >
                        {z.email}
                      </button>
                    </th>
                    <td className="liste__einzeilig">{alsZeitpunkt(z.angelegtAm)}</td>
                    <td>
                      {z.kunde !== null
                        ? `${z.kunde.kundennummer} ${z.kunde.name}`
                        : `${String(z.kandidaten.length)} Treffer`}
                    </td>
                    <td>
                      <span className={markenklasse(standart(z))}>{stand(z)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>

      {nachfrage !== null && (
        <Zuordnungsrueckfrage
          nachfrage={nachfrage}
          entscheiden={entscheiden}
          beiSchliessen={() => {
            setzeNachfrage(null);
          }}
        />
      )}
    </>
  );
}

/** Die Rückfrage vor Zuordnen, Verwerfen und Zurücknehmen (CRM-104, WEB-10 i.d.F. AeA-37). */
function Zuordnungsrueckfrage({
  nachfrage,
  entscheiden,
  beiSchliessen,
}: {
  readonly nachfrage: Nachfrage;
  readonly entscheiden: (vorgang: () => Promise<Kontozuordnung>) => Promise<void>;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const { fall } = nachfrage;

  if (nachfrage.art === 'zuordnen') {
    const k = nachfrage.kandidat;
    return (
      <Rueckfrage
        frage="Konto zuordnen?"
        unterzeile={fall.email}
        tat="Zuordnen"
        beiBestaetigen={() => entscheiden(() => zuordnen(fall.id, k.kunde))}
        beiSchliessen={beiSchliessen}
      >
        <p>
          Das Online-Konto {fall.email} gehört danach zu {k.kundennummer} {k.name}. Die Zuordnung
          steht im Protokoll und lässt sich mit Grund zurücknehmen.
        </p>
      </Rueckfrage>
    );
  }

  if (nachfrage.art === 'verwerfen') {
    return (
      <Rueckfrage
        frage="Fall verwerfen?"
        unterzeile={fall.email}
        tat="Verwerfen"
        beiBestaetigen={() => entscheiden(() => verwerfen(fall.id, nachfrage.grund))}
        beiSchliessen={beiSchliessen}
      >
        <p>
          Das Online-Konto wird keinem der {String(fall.kandidaten.length)} Kunden zugeordnet; der
          Kunde entsteht mit der ersten Buchung.
        </p>
        <p>Grund: „{nachfrage.grund}“</p>
      </Rueckfrage>
    );
  }

  return (
    <Rueckfrage
      frage="Zuordnung zurücknehmen?"
      unterzeile={fall.email}
      tat="Zurücknehmen"
      beiBestaetigen={() => entscheiden(() => zuruecknehmen(fall.id, nachfrage.grund))}
      beiSchliessen={beiSchliessen}
    >
      <p>
        Das Online-Konto gehört danach nicht mehr zu{' '}
        {fall.kunde === null ? 'diesem Kunden' : `${fall.kunde.kundennummer} ${fall.kunde.name}`}.
        Weder die Automatik noch die erste Buchung verbinden die beiden wieder.
      </p>
      <p>Grund: „{nachfrage.grund}“</p>
    </Rueckfrage>
  );
}
